/**
 * Switch toggle field component.
 */

import { useTranslation } from "react-i18next";
import { Switch } from "~/components/ui/switch";
import { Label } from "~/components/ui/label";
import { AlertCircle, HelpCircle } from "lucide-react";
import type { FieldProps } from "./types";

export function SwitchField({
  field,
  value,
  touched,
  error,
  label,
  description,
  extendedDescription,
  onChange,
}: FieldProps) {
  const { t } = useTranslation();
  const hasError = touched && !!error;
  const errorId = `${field.name}-error`;
  const descriptionId = `${field.name}-description`;
  const extendedDescriptionId = `${field.name}-extended-description`;

  // Build aria-describedby based on available content
  const ariaDescribedByParts: string[] = [];
  if (description) ariaDescribedByParts.push(descriptionId);
  if (extendedDescription) ariaDescribedByParts.push(extendedDescriptionId);
  if (hasError) ariaDescribedByParts.push(errorId);
  const ariaDescribedBy = ariaDescribedByParts.length > 0 ? ariaDescribedByParts.join(" ") : undefined;

  return (
    <div className="space-y-2 animate-in">
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="space-y-0.5">
          <Label htmlFor={field.name} className="text-base">
            {label}
          </Label>
          {description && (
            <p id={descriptionId} className="text-sm text-muted-foreground">
              {description}
            </p>
          )}
          {extendedDescription && (
            <details className="text-sm">
              <summary className="text-muted-foreground cursor-pointer hover:text-foreground flex items-center gap-1 select-none">
                <HelpCircle className="h-3 w-3" aria-hidden="true" />
                {t("common.moreInfo", "More information")}
              </summary>
              <p
                id={extendedDescriptionId}
                className="mt-2 text-muted-foreground pl-4 border-l-2 border-muted"
              >
                {extendedDescription}
              </p>
            </details>
          )}
        </div>
        <Switch
          id={field.name}
          checked={value as boolean}
          onCheckedChange={(checked: boolean) => onChange(field.name, checked)}
          aria-describedby={ariaDescribedBy}
        />
      </div>
      {hasError && error && (
        <p
          id={errorId}
          role="alert"
          aria-live="polite"
          className="text-sm text-destructive flex items-center gap-1"
        >
          <AlertCircle className="h-3 w-3" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}
